// =================================================================
// Store: Patient slice (PSD §9.1.1 / TODO Task 1.10, 2.15)
// =================================================================
import type { StateCreator } from 'zustand';
import {
  Agent,
  Indication,
  Sex,
  FluidStatus,
  type PatientInput,
} from '@/types';
import { AGENT_PARAMS } from '@/config/agentParams';
import { resolveIndicationTargets } from '@/config/indications';
import { validatePatientInput } from '@/engine/validation';
import type { PatientSlice, StoreState } from './types';

const DEFAULT_PATIENT: PatientInput = {
  ageYears: 67,
  sex: Sex.Male,
  heightCm: 175,
  actualWeightKg: 82.5,
  scrMgDl: 1.4,
  scrTimestamp: new Date().toISOString(),
  indication: Indication.GramNegativeSepsis,
  fluidStatus: FluidStatus.Euvolemic,
  bunMgDl: 22,
};

export const createPatientSlice: StateCreator<StoreState, [], [], PatientSlice> = (set, get) => ({
  patient: DEFAULT_PATIENT,
  validationErrors: {},
  agentParams: resolveIndicationTargets(
    AGENT_PARAMS[Agent.Gentamicin],
    DEFAULT_PATIENT.indication,
    'extended-interval' as never,
  ),

  setPatientField: (field, value) => {
    set((state) => {
      if (!state.patient) return state;
      const patient = { ...state.patient, [field]: value } as PatientInput;
      return { patient, validationErrors: validatePatientInput(patient).errors };
    });
    get().scheduleComputeAll();
  },

  setAgent: (agent) => {
    set((state) => ({
      agentParams: resolveIndicationTargets(
        AGENT_PARAMS[agent],
        state.patient?.indication ?? Indication.GramNegativeSepsis,
        state.strategyConfig.strategy,
      ),
    }));
    get().scheduleComputeAll();
  },

  setIndication: (indication) => {
    set((state) => ({
      patient: state.patient ? { ...state.patient, indication } : state.patient,
      // E-DF-2: derive from pristine base params — passing state.agentParams would re-apply the
      // intensity multiplier to an already-multiplied traditionalDoseMgKg, compounding on each change.
      agentParams: resolveIndicationTargets(
        AGENT_PARAMS[state.agentParams.agent],
        indication,
        state.strategyConfig.strategy,
      ),
    }));
    get().scheduleComputeAll();
  },

  validatePatient: () => {
    const { patient } = get();
    if (!patient) return false;
    const result = validatePatientInput(patient);
    set({ validationErrors: result.errors });
    return result.isValid;
  },

  resetPatient: () => set({ patient: null, validationErrors: {} }),

  setContextModifier: (type, active) => {
    set((state) => {
      if (!state.patient) return state;
      const existing = state.patient.contextModifiers ?? [];
      const others = existing.filter((m) => m.type !== type);
      const modifiers = [...others, { type, active }];
      return { patient: { ...state.patient, contextModifiers: modifiers } };
    });
    get().scheduleComputeAll();
  },

  setConcurrentNephrotoxins: (toxins) => {
    set((state) => ({
      patient: state.patient ? { ...state.patient, concurrentNephrotoxins: toxins } : state.patient,
    }));
    get().scheduleComputeAll();
  },
});
